import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Link } from "react-router-dom";
import { 
  Building, 
  Hammer, 
  Truck, 
  Wrench,
  Zap,
  Shield,
  Users,
  Phone,
  Mail,
  Paintbrush,
  Home,
  Droplets,
  Flame,
  HardHat,
  Ruler
} from "lucide-react";
import ontarioConstructionImage from "@/assets/ontario-construction-site-realistic.jpg";
import generalContractorImage from "@/assets/general-contractor-blueprints.jpg";
import skilledTradesImage from "@/assets/skilled-trades-team.jpg";
import heavyEquipmentImage from "@/assets/heavy-equipment-site.jpg";
import steelErectorImage from "@/assets/steel-erector.jpg";
import designEngineeringImage from "@/assets/design-engineering.jpg";
import safetyOfficerImage from "@/assets/safety-officer.jpg";

// Skilled trades images
import carpenterImage from "@/assets/carpenter-ontario.jpg";
import electricianImage from "@/assets/electrician-ontario.jpg";
import plumberImage from "@/assets/plumber-ontario.jpg";
import masonImage from "@/assets/mason-brickwork.jpg";
import hvacImage from "@/assets/hvac-ontario.jpg";
import welderImage from "@/assets/welder-fabrication.jpg";
import drywallImage from "@/assets/drywall-installation-work.jpg";
import paintingImage from "@/assets/interior-painting-residential.jpg";
import roofingImage from "@/assets/roofing-installation.jpg";
import flooringImage from "@/assets/hardwood-flooring-install.jpg";
import deckImage from "@/assets/deck-construction-ontario.jpg";
import concreteImage from "@/assets/concrete-ontario.jpg";

const Gallery = () => {
  const faqData = {
    faqs: [
      {
        question: "What types of construction jobs does Blue Knight Contracting offer?",
        answer: "We provide general contracting, skilled trades, heavy equipment, specialty contracting, design/engineering, and safety services across Ontario for residential, commercial, and industrial projects."
      },
      {
        question: "Can I hire multiple trades and services through Blue Knight?",
        answer: "Yes. We provide a fully integrated solution so all trades and services are managed under one contract with seamless coordination and project management."
      },
      {
        question: "Do you serve Northern Ontario regions like Sudbury and Thunder Bay?",
        answer: "Absolutely. We proudly serve communities in Northern Ontario as well as major cities in Southern and Eastern Ontario with complete construction services."
      },
      {
        question: "How do I get started with Blue Knight Contracting?",
        answer: "Contact us for a free consultation and quote. We'll discuss your project needs and provide a comprehensive proposal for all required services."
      }
    ]
  };

  const services = [
    {
      title: "General Contracting",
      description: "Complete project management for residential, commercial, and industrial builds with subcontractor coordination.",
      image: generalContractorImage,
      alt: "Ontario general contractor managing construction project",
      icon: Building,
      link: "/services/general-contracting",
      features: ["Complete project management", "Residential, commercial, and industrial builds", "Subcontractor coordination"]
    },
    {
      title: "Skilled Trades",
      description: "Carpentry, Electrical, Plumbing, Masonry, HVAC, Welding, Drywall, Painting, Roofing, Glazing, Tile Setting, and Flooring services.",
      image: skilledTradesImage,
      alt: "Ontario skilled trades team working on residential construction project",
      icon: Hammer,
      link: "/services/skilled-trades",
      features: ["Carpentry, Electrical, Plumbing, Masonry", "HVAC, Welding, Drywall, Painting", "Roofing, Glazing, Tile Setting, Flooring"]
    },
    {
      title: "Heavy Equipment & Site Work",
      description: "Excavators, bulldozers, cranes, concrete finishing, demolition, grading, paving, and pile driving services.",
      image: heavyEquipmentImage,
      alt: "Ontario heavy equipment operators working on excavation project",
      icon: Truck,
      link: "/services/heavy-equipment",
      features: ["Excavators, bulldozers, and cranes", "Concrete finishing & demolition", "Grading, paving, and pile driving"]
    },
    {
      title: "Specialty Contracting",
      description: "Steel erection, ironwork, fire protection, sprinklers, insulation, waterproofing, low voltage cabling, and solar panel installation.",
      image: steelErectorImage,
      alt: "Ontario steel erector installing building framework",
      icon: Wrench,
      link: "/services/specialty-contracting",
      features: ["Steel erection & ironwork", "Fire protection & sprinklers", "Insulation & waterproofing", "Low voltage cabling & solar panel installation"]
    },
    {
      title: "Design & Engineering Support",
      description: "Architecture, civil engineering, structural and MEP engineering, surveying, and CAD/BIM modeling services.",
      image: designEngineeringImage,
      alt: "Ontario architects and engineers planning construction project",
      icon: Zap,
      link: "/services/design-engineering",
      features: ["Architecture & civil engineering", "Structural & MEP engineering", "Surveying & CAD/BIM modeling"]
    },
    {
      title: "Support & Safety Roles",
      description: "Safety officers, OSHA specialists, construction laborers, cleanup crews, materials handling, and logistics support.",
      image: safetyOfficerImage,
      alt: "Ontario safety officer ensuring compliance on construction site",
      icon: Shield,
      link: "/services/support-safety",
      features: ["Safety officers & OSHA specialists", "Construction laborers & cleanup crews", "Materials handling & logistics support"]
    }
  ];

  const skilledTradesGallery = [
    {
      title: "Carpentry Services",
      description: "Expert framing, trim work, cabinetry, and custom woodwork for residential and commercial projects across Ontario.",
      image: carpenterImage,
      alt: "Professional carpenter working on framing and trim in Ontario",
      icon: Hammer,
      link: "/services/carpenter"
    },
    {
      title: "Electrical Services",
      description: "Licensed electricians providing installations, repairs, panel upgrades, and commercial electrical work throughout Ontario.",
      image: electricianImage,
      alt: "Licensed electrician performing electrical panel installation in Ontario",
      icon: Zap,
      link: "/services/electrician"
    },
    {
      title: "Plumbing Services",
      description: "Complete plumbing solutions including installations, repairs, drain cleaning, and water heater services across Ontario.",
      image: plumberImage,
      alt: "Professional plumber installing pipes and fixtures in Ontario",
      icon: Droplets,
      link: "/services/plumber"
    },
    {
      title: "Masonry Services",
      description: "Brick, stone, and concrete masonry work for foundations, walls, chimneys, and decorative stonework in Ontario.",
      image: masonImage,
      alt: "Skilled mason laying bricks for residential construction in Ontario",
      icon: Building,
      link: "/services/masonry"
    },
    {
      title: "HVAC Services",
      description: "Heating, ventilation, and air conditioning installation, maintenance, and repair for homes and businesses in Ontario.",
      image: hvacImage,
      alt: "HVAC technician installing furnace and air conditioning system in Ontario",
      icon: Flame,
      link: "/services/hvac"
    },
    {
      title: "Welding & Fabrication",
      description: "Custom metal fabrication, structural welding, and mobile welding services for commercial and industrial projects.",
      image: welderImage,
      alt: "Professional welder performing structural steel welding in Ontario",
      icon: Wrench,
      link: "/services/welding"
    },
    {
      title: "Drywall Services",
      description: "Professional drywall installation, taping, mudding, and finishing for smooth walls and ceilings across Ontario.",
      image: drywallImage,
      alt: "Drywall installer hanging and finishing drywall in Ontario home",
      icon: HardHat,
      link: "/services/drywall"
    },
    {
      title: "Painting Services",
      description: "Interior and exterior painting for residential and commercial properties with premium finishes throughout Ontario.",
      image: paintingImage,
      alt: "Professional painters applying interior paint in Ontario residential home",
      icon: Paintbrush,
      link: "/services/painting"
    },
    {
      title: "Roofing Services",
      description: "Complete roofing solutions including installations, repairs, replacements for residential and commercial properties.",
      image: roofingImage,
      alt: "Roofing crew installing new shingles on Ontario residential roof",
      icon: Home,
      link: "/services/roofing"
    },
    {
      title: "Flooring Services",
      description: "Hardwood, tile, vinyl, and carpet installation with expert craftsmanship for homes and businesses in Ontario.",
      image: flooringImage,
      alt: "Flooring installer laying hardwood floors in Ontario home",
      icon: Ruler,
      link: "/services/flooring"
    },
    {
      title: "Deck & Fence Services",
      description: "Custom deck construction and fence installation for residential properties across Ontario with quality materials.",
      image: deckImage,
      alt: "Deck builder constructing custom deck for Ontario home",
      icon: Hammer,
      link: "/services/deck-fence"
    },
    {
      title: "Concrete & Asphalt",
      description: "Concrete pouring, asphalt paving, driveways, and parking lots for residential and commercial properties.",
      image: concreteImage,
      alt: "Concrete crew pouring foundation for Ontario construction project",
      icon: Truck,
      link: "/services/asphalt-paving-concrete"
    }
  ];

  const schemaData = {
    images: services.map(service => ({
      "@type": "ImageObject",
      "url": service.image,
      "description": service.alt,
      "name": service.title
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <SchemaMarkup type="Service" data={{ serviceType: "Ontario Construction Gallery", services: schemaData.images }} />
      <SchemaMarkup type="FAQ" data={faqData} />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Ontario Construction Services Gallery – Blue Knight Contracting and Construction
          </h1>
          <div className="mb-8">
            <img 
              src={ontarioConstructionImage} 
              alt="Ontario construction project featuring skilled trades, heavy equipment, and general contracting services"
              className="w-full max-w-4xl mx-auto h-auto rounded-lg shadow-xl"
            />
          </div>
          <Button variant="hero" size="lg" className="bg-primary-foreground text-white hover:bg-primary-foreground/90">
            <Phone className="mr-2 h-5 w-5" />
            Start Your Project Today
          </Button>
        </div>
      </section>

      {/* Explore Our Full Range of Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Explore Our Full Range of Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
              Blue Knight Contracting and Construction provides a complete suite of contracting and construction services 
              across Ontario. From general contracting and project management to skilled trades, heavy equipment, specialty 
              contracting, design/engineering, and safety support, we are your trusted partner for residential, commercial, 
              and industrial projects.
            </p>
            <p className="text-lg text-muted-foreground mt-4">
              We proudly serve Toronto, Ottawa, Mississauga, Hamilton, London, Windsor, Kingston, Barrie, Sudbury, 
              Thunder Bay, and communities across Ontario.
            </p>
          </div>
        </div>
      </section>

      {/* Service Gallery */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Service Gallery
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Browse our services below. Each image links to a detailed service page where you can learn more about 
              our comprehensive construction and contracting capabilities.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20 overflow-hidden">
                <div className="aspect-video relative">
                  <img 
                    src={service.image} 
                    alt={service.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm p-2 rounded-lg">
                    <service.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                </div>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-sm">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary transition-colors">
                      👉 Learn More About {service.title}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Skilled Trades Gallery */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Skilled Trades Gallery
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore our comprehensive skilled trades services. Each trade features expert craftsmen delivering quality workmanship across Ontario.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {skilledTradesGallery.map((trade, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="aspect-[4/3] relative overflow-hidden">
                  <img 
                    src={trade.image} 
                    alt={trade.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <trade.icon className="h-5 w-5" />
                      <h3 className="font-bold text-lg">{trade.title}</h3>
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {trade.description}
                  </p>
                  <Link to={trade.link}>
                    <Button variant="outline" size="sm" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      View {trade.title.replace(' Services', '')} Services →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Blue Knight Contracting */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Choose Blue Knight Contracting?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Building className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">One Trusted Partner</h3>
                <p className="text-muted-foreground">
                  Complete solution for all construction needs in Ontario under one trusted partnership.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Hammer className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Full-Service Coverage</h3>
                <p className="text-muted-foreground">
                  Design, trades, heavy equipment, and safety services all coordinated seamlessly.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Experienced Team</h3>
                <p className="text-muted-foreground">
                  Serving major Ontario cities and surrounding towns with proven expertise.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Quality & Safety</h3>
                <p className="text-muted-foreground">
                  Commitment to quality, safety, and on-time delivery on every project.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Frequently Asked Questions (AI + SEO Optimized)
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Common questions about our comprehensive construction services across Ontario.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {faqData.faqs.map((faq, index) => (
              <Card key={index} className="border-2 border-primary/10">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-lg mb-3 text-primary">
                    Q: {faq.question}
                  </h3>
                  <p className="text-muted-foreground">
                    A: {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Start Your Project Today */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Start Your Project Today
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Ready to bring your project to life? Contact Blue Knight Contracting and Construction today 
            to schedule a consultation and discover how our comprehensive services can meet your construction needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button variant="hero" size="lg" className="text-lg px-8 py-4 bg-primary-foreground text-primary hover:bg-primary-foreground/90">
              <Phone className="mr-2 h-5 w-5" />
              Call (416) 555-BLUE
            </Button>
            <Button variant="contact" size="lg" className="text-lg px-8 py-4">
              <Mail className="mr-2 h-5 w-5" />
              Schedule Consultation
            </Button>
          </div>
          <div className="text-lg opacity-80">
            <p>📍 Serving Ontario: Toronto, Ottawa, Mississauga, Hamilton, London, Windsor, Kingston, Barrie, Sudbury, Thunder Bay</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Gallery;